import { useState, useEffect, useRef, useCallback } from 'react';
import { Robot, PaperPlaneTilt, Trash, X, CaretDown, DownloadSimple, ArrowsOutSimple, ArrowsInSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Markdown from 'react-markdown';

const AI_LOGO = "https://static.prod-images.emergentagent.com/jobs/595901cb-0888-4a96-8fab-b1a18d0ee521/images/ce46aabfdf7944a78ff3c1b227f06bef2e713b027366c9512236cd84098f9058.png";

const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesBoxRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesBoxRef.current) {
      messagesBoxRef.current.scrollTop = messagesBoxRef.current.scrollHeight;
    }
  };

  useEffect(() => { scrollToBottom(); }, [messages, loading]);
  useEffect(() => { if (open && window.innerWidth >= 640 && inputRef.current) inputRef.current.focus(); }, [open]);

  // Verrouille le défilement de la page derrière le chat (mobile)
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const loadHistory = useCallback(async () => {
    if (historyLoaded) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/history`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.map(m => ({ role: m.role, content: m.content })));
        setHistoryLoaded(true);
      }
    } catch (err) { console.warn('Historique IA indisponible:', err?.message); }
  }, [historyLoaded]);

  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
      if (res.ok) {
        const data = await res.json();
        let responseText = data.response;
        // Check for report download links in actions
        const downloadLinks = [];
        if (data.actions_executed?.length > 0) {
          data.actions_executed.forEach(r => {
            if (r.startsWith('RAPPORT_DOWNLOAD:')) {
              const reportId = r.replace('RAPPORT_DOWNLOAD:', '');
              downloadLinks.push(reportId);
            }
          });
          const nonDownloads = data.actions_executed.filter(r => !r.startsWith('RAPPORT_DOWNLOAD:'));
          if (nonDownloads.length > 0) {
            toast.success(`${nonDownloads.length} action(s) exécutée(s)`);
          }
        }
        setMessages(prev => [...prev, {
          role: 'assistant', content: responseText,
          downloads: downloadLinks,
        }]);
        if (downloadLinks.length > 0) {
          toast.success('Rapport généré ! Cliquez pour télécharger.');
        }
      } else {
        const err = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: `Erreur : ${err.detail || 'Réessayez'}` }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Erreur de connexion. Réessayez.' }]);
    } finally { setLoading(false); }
  };

  const clearHistory = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/history`, { method: 'DELETE', credentials: 'include' });
      setMessages([]);
      toast.success('Historique effacé');
    } catch { toast.error('Erreur'); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-assistant-button"
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 h-14 w-14 rounded-full shadow-lg shadow-[#2563EB]/30 flex items-center justify-center hover:scale-110 transition-transform overflow-hidden"
        >
          <img src={AI_LOGO} alt="IA Gazelle" className="h-14 w-14 object-cover" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div className={`fixed z-50 flex flex-col bg-[#0F172A] border border-[#1E293B] shadow-2xl shadow-black/40 overflow-hidden h-[100dvh] ${expanded ? 'inset-x-0 top-0 sm:inset-10 sm:h-auto rounded-none sm:rounded-xl' : 'inset-x-0 top-0 sm:inset-auto sm:h-auto sm:bottom-6 sm:right-6 sm:w-[420px] sm:max-h-[600px] rounded-none sm:rounded-xl'}`} data-testid="ai-chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white">
            <div className="flex items-center gap-2">
              <img src={AI_LOGO} alt="IA" className="h-8 w-8 rounded-full object-cover" />
              <div>
                <p className="font-semibold text-sm leading-tight">Assistant IA Gazelle</p>
                <p className="text-[10px] text-white/70">Claude • Opérationnel</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setExpanded(!expanded)} className="p-1.5 rounded hover:bg-white/20 transition-colors" title={expanded ? 'Réduire' : 'Agrandir'} data-testid="ai-expand-button">{expanded ? <ArrowsInSimple size={16} /> : <ArrowsOutSimple size={16} />}</button>
              <button onClick={clearHistory} className="p-1.5 rounded hover:bg-white/20 transition-colors" title="Effacer l'historique"><Trash size={16} /></button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-white/20 transition-colors" title="Fermer"><CaretDown size={16} /></button>
            </div>
          </div>

          {/* Messages */}
          <div ref={messagesBoxRef} className={`flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-4 space-y-3 min-h-0 ${expanded ? '' : 'sm:min-h-[300px] sm:max-h-[440px]'}`} style={{ WebkitOverflowScrolling: 'touch' }}>
            {messages.length === 0 && (
              <div className="text-center py-8">
                <img src={AI_LOGO} alt="IA" className="h-12 w-12 rounded-full mx-auto mb-3" />
                <p className="text-[#94A3B8] text-sm font-medium">Bonjour ! Je suis votre assistant IA.</p>
                <p className="text-[#64748B] text-xs mt-1">Posez-moi une question ou demandez-moi d'effectuer une action.</p>
                <div className="mt-4 space-y-1.5">
                  {[
                    "Combien d'agents sont disponibles ?",
                    "Quel est l'état du stock ?",
                    "Résumé financier du mois",
                  ].map((q, i) => (
                    <button key={q} onClick={() => { setInput(q); }} className="block w-full text-left px-3 py-2 text-xs text-[#94A3B8] bg-[#1E293B]/50 rounded-md border border-[#334155]/50 hover:border-[#2563EB]/50 hover:text-[#F8FAFC] transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] min-w-0 break-words px-3 py-2 rounded-lg text-sm ${
                  msg.role === 'user'
                    ? 'bg-[#2563EB] text-white rounded-br-sm whitespace-pre-wrap'
                    : 'bg-[#1E293B] text-[#F8FAFC] rounded-bl-sm border border-[#334155]/50 ai-msg'
                }`}>
                  {msg.role === 'user' ? msg.content : (
                    <Markdown
                      components={{
                        h1: ({children}) => <p className="text-base font-bold mb-1">{children}</p>,
                        h2: ({children}) => <p className="text-sm font-bold mb-1">{children}</p>,
                        h3: ({children}) => <p className="text-sm font-semibold mb-1">{children}</p>,
                        p: ({children}) => <p className="mb-1.5 last:mb-0 leading-relaxed">{children}</p>,
                        strong: ({children}) => <span className="font-semibold text-[#60A5FA]">{children}</span>,
                        em: ({children}) => <span className="italic text-[#94A3B8]">{children}</span>,
                        ul: ({children}) => <ul className="ml-3 mb-1.5 space-y-0.5">{children}</ul>,
                        ol: ({children}) => <ol className="ml-3 mb-1.5 space-y-0.5 list-decimal">{children}</ol>,
                        li: ({children}) => <li className="leading-relaxed">• {children}</li>,
                        table: ({children}) => <div className="overflow-x-auto my-1.5"><table className="w-full text-xs border-collapse">{children}</table></div>,
                        thead: ({children}) => <thead className="bg-[#334155]/50">{children}</thead>,
                        th: ({children}) => <th className="px-2 py-1 text-left text-[#94A3B8] font-medium border-b border-[#334155]">{children}</th>,
                        td: ({children}) => <td className="px-2 py-1 border-b border-[#334155]/30">{children}</td>,
                        blockquote: ({children}) => <div className="border-l-2 border-[#F59E0B] pl-2 my-1 text-[#F59E0B] text-xs">{children}</div>,
                        hr: () => <hr className="border-[#334155] my-1.5" />,
                        code: ({children}) => <code className="bg-[#334155] px-1 rounded text-xs text-[#10B981]">{children}</code>,
                      }}
                    >
                      {msg.content}
                    </Markdown>
                  )}
                  {msg.downloads?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {msg.downloads.map((id) => (
                        <a key={id} href={`${process.env.REACT_APP_BACKEND_URL}/api/ai/reports/${id}`}
                          target="_blank" rel="noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-[#10B981] hover:bg-[#059669] text-white rounded-md text-xs font-medium transition-colors no-underline">
                          <DownloadSimple size={16} weight="bold" /> Télécharger le rapport
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1E293B] border border-[#334155]/50 rounded-lg rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-[#2563EB] rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                    <span className="w-2 h-2 bg-[#2563EB] rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                    <span className="w-2 h-2 bg-[#2563EB] rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#1E293B] bg-[#0B1120]" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Écrivez votre message..."
                rows={1}
                enterKeyHint="send"
                className="flex-1 bg-[#1E293B] border border-[#334155] rounded-lg px-3 py-2.5 text-base sm:text-sm text-[#F8FAFC] placeholder-[#64748B] resize-none focus:outline-none focus:border-[#2563EB] transition-colors"
                data-testid="ai-chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-3 rounded-lg disabled:opacity-40"
                data-testid="ai-chat-send"
              >
                <PaperPlaneTilt size={18} weight="fill" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
